import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { AnimateSharedLayout, motion } from 'framer-motion';
import { wrap } from 'popmotion';
import { spaceMixin } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import { event } from '@zillow/universal-analytics';
import CarouselItem from './CarouselItem.js';
import CarouselDot from './CarouselDot.js';
import CarouselNavigation from './CarouselNavigation.js';
import triggerTabClickEvent from '../../utils/clickstream/tab-click.js';

var TabsContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-e82pwv-0"
})(["display:flex;justify-content:center;margin-bottom:", ";flex-wrap:wrap;"], spaceMixin('sm'));
var DotsContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-e82pwv-1"
})(["display:flex;justify-content:center;flex-wrap:nowrap;margin-top:", ";"], spaceMixin('sm'));
var Slider = /*#__PURE__*/styled(motion.div).withConfig({
  componentId: "sc-e82pwv-2"
})(["width:100%;display:flex;flex-direction:row;"]);
var SlidesDotsAndArrowsContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-e82pwv-3"
})(["position:relative;display:flex;flex-direction:row;min-height:300px;overflow:hidden;"]);

/**
 * A carousel container that is able to hold any type of media content.
 *
 */
var Carousel = function Carousel(_ref) {
  var children = _ref.children,
    showTabs = _ref.showTabs,
    showDots = _ref.showDots,
    showArrows = _ref.showArrows,
    id = _ref.id;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    page = _useState2[0],
    setPage = _useState2[1];
  var slideCount = React.Children.count(children);
  var getSlideIndex = function getSlideIndex(targetPage) {
    return wrap(0, slideCount, targetPage);
  };
  var activeSlideIndex = getSlideIndex(page);
  var triggerClickStreamEvent = function triggerClickStreamEvent(blockDisplayedTxt, targetPage) {
    return event({
      // https://dataportal.zillowgroup.net/event-registry/2048
      envelope: {
        event_type_id: '2048',
        event_type_version_id: '1',
        event_template_id: '126',
        event_template_version_id: '1'
        // Master ID: 2284.2.126.1
      },
      clickstream_trigger: {
        trigger_type_nm: 'interaction',
        trigger_location_nm: 'cms',
        trigger_source_nm: 'cms_carousel',
        trigger_object_nm: 'no_trigger_object'
      },
      semantic: {
        semantic_event_nm: 'cms_click'
      },
      cms_info: {
        block_id: id || '',
        block_type_cd: 'carousel',
        block_displayed_txt: blockDisplayedTxt,
        // We don't want zero-based index, so add 1.
        slide_number: getSlideIndex(targetPage) + 1
      }
    });
  };
  return /*#__PURE__*/React.createElement("div", null, showTabs && /*#__PURE__*/React.createElement(AnimateSharedLayout, null, /*#__PURE__*/React.createElement(TabsContainer, null, React.Children.map(children, function (element, index) {
    return /*#__PURE__*/React.cloneElement(element, {
      privateRenderType: 'tab',
      // The below props will get passed to <CarouselTab>
      isActive: activeSlideIndex === index,
      onClick: yieldCallback(function () {
        setPage(index);
        triggerTabClickEvent({
          id: element.props.key,
          label: element.props.id,
          isCarouselTab: true
        });
      })
    });
  }))), /*#__PURE__*/React.createElement(SlidesDotsAndArrowsContainer, null, /*#__PURE__*/React.createElement(Slider, {
    animate: {
      x: -activeSlideIndex * 100 + "%"
    },
    transition: {
      x: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  }, React.Children.toArray(children).map(function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      privateRenderType: 'slide',
      showArrows: showArrows
    });
  })), showArrows && /*#__PURE__*/React.createElement(CarouselNavigation, {
    onLeftButtonClick: function onLeftButtonClick() {
      return setPage(function (currentPage) {
        var nextPage = currentPage - 1;
        triggerClickStreamEvent('previous', nextPage);
        return nextPage;
      });
    },
    onRightButtonClick: function onRightButtonClick() {
      return setPage(function (currentPage) {
        var nextPage = currentPage + 1;
        triggerClickStreamEvent('next', nextPage);
        return nextPage;
      });
    }
  })), showDots && /*#__PURE__*/React.createElement(DotsContainer, null, React.Children.map(children, function (_, index) {
    return /*#__PURE__*/React.createElement(CarouselDot, {
      key: index.toString(),
      isActive: activeSlideIndex === index,
      type: "button",
      onClick: yieldCallback(function () {
        setPage(index);
        triggerClickStreamEvent('jump_dot', index);
      })
    });
  })));
};
Carousel.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An array of Carousel content to be displayed
   */
  children: PropTypes.node.isRequired,
  /**
   * Flag to display pagination dots
   */
  showDots: PropTypes.bool,
  /**
   * Flag to display pagination tabs
   */
  showTabs: PropTypes.bool,
  /**
   * Flag to display navigation arrows
   */
  showArrows: PropTypes.bool,
  id: PropTypes.string
} : {};
Carousel.defaultProps = {
  showDots: true,
  showTabs: true,
  showArrows: true,
  id: ''
};
Carousel.Item = CarouselItem;

/** @component */
var Carousel$1 = Carousel;

export { DotsContainer, SlidesDotsAndArrowsContainer, TabsContainer, Carousel$1 as default };
